import { Link, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from "react";
import {
    Button,
    Card,
    CardContent
} from "@mui/material";
import { MessageSquare, Menu } from "lucide-react";
import Logo from "../../Assets/logo.png";
import "./decisions.css";
import Sidebar from './SideBar/Sidebar';
import Synthese from './SectionContent/Synthese';
import FaitsProcedures from './SectionContent/Fait_procedure';
import Motifs from './SectionContent/Motif';
import ReferencesLegales from './SectionContent/ReferenceLegal';
import Resume from './SectionContent/Resume';
import DecisionComplete from './SectionContent/DecisionComplete';
import MobileHeader from './Mobile/MobileHeader';
import MobileMenu from './Mobile/MobileMenu';

import './Mobile/MobileHeader.css';
import './Mobile/MobileMenu.css';

const mainContentStyle = {
    maxHeight: "calc(100vh - 80px)",
    overflowY: "auto",
    padding: "20px",
    scrollBehavior: "smooth"
};

const DetailDecision = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState("synthese");
    const [decisionData, setDecisionData] = useState(null);
    const [decisionSimilaireData, setDecisionSimilaireData] = useState(null);
    const [decisionLieeData, setDecisionLieeData] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchInput, setSearchInput] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    const sidebarRef = useRef(null);
    const mainContentRef = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setScreenSize({
                width: window.innerWidth,
                height: window.innerHeight
            });

            if (window.innerWidth > 768) {
                setMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.mobile-menu') || event.target.closest('.mobile-menu-button')) {
                return;
            }
            if (mobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setMobileMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        const fetchDecisionData = async () => {
            if (!id) return;

            setLoading(true);
            setError(null);

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/recouv/decision-detail/${id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    }
                });

                if (!response.ok) {
                    throw new Error("Une erreur est survenue lors de la récupération des données.");
                }

                const data = await response.json();
                setDecisionData(data);
            } catch (err) {
                setError("Erreur lors du chargement des données");
                console.error("Error fetching decision data:", err);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchDecisionData();
        }
    }, [id]);

    useEffect(() => {
        const fetchDecisionSimilaire = async () => {
            if (!id) return;

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/recouv/decision-similar/${id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    }
                });

                if (!response.ok) {
                    throw new Error("Une erreur est survenue lors de la récupération des décisions similaires.");
                }

                const data = await response.json();
                setDecisionSimilaireData(data.results || data);
            } catch (err) {
                console.error("Error fetching similar decisions:", err);
                setDecisionSimilaireData([]);
            }
        };

        if (id) {
            fetchDecisionSimilaire();
        }
    }, [id]);

    useEffect(() => {
        const fetchDecisionLiee = async () => {
            if (!id) return;

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/recouv/decision-linked/${id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    }
                });

                if (!response.ok) {
                    throw new Error("Une erreur est survenue lors de la récupération des décisions liées.");
                }

                const data = await response.json();
                setDecisionLieeData(data.results || data);
            } catch (err) {
                console.error("Error fetching linked decisions:", err);
                setDecisionLieeData([]);
            }
        };

        if (id) {
            fetchDecisionLiee();
        }
    }, [id]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchInput.trim()) {
            navigate(`/allresult?query=${encodeURIComponent(searchInput.trim())}`);
        }
    };

    const handleLogin = () => {
        window.location.href = "https://datagreffe.ci/auth/login";
        console.log("Connexion");
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const scrollToSection = (sectionId) => {
        if (mainContentRef.current) {
            const section = document.getElementById(sectionId);
            if (section) {
                const offsetTop = section.offsetTop - mainContentRef.current.offsetTop;
                mainContentRef.current.scrollTo({
                    top: offsetTop,
                    behavior: 'smooth'
                });
            }
        }
    };

    const handleMenuClick = (key) => {
        console.log(`Menu cliqué: ${key}`);

        setActiveTab(key);
        scrollToSection(key);

    };

    function setCookie(name, value, days) {
        let expires = "";
        if (days) {
            const date = new Date();
            date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
            expires = "; expires=" + date.toUTCString();
        }
        document.cookie = name + "=" + encodeURIComponent(value) + expires + "; path=/";
    }

    const handleLaunchChat = () => {
        setCookie('pendingDecisionChatId', id, 1); // Cookie valide pour 1 jour
        window.location.href = "https://datagreffe.ci/auth/login?pendingDecisionId=" + encodeURIComponent(id);
    };

    return (
        <div className="justice-container">
            <header className="search-header">
                <div className="logo-container">
                    <Link to="/">
                        <img src={Logo} alt="Logo" className="justice-logo" />
                    </Link>
                    <h1 className="text-xl font-bold">Datagreffe justice</h1>
                </div>

                <div className="search-container">
                    <form onSubmit={handleSearch} className="search-form">
                        <div className="search-input-container">
                            <span className="search-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                            </span>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder={screenSize.width <= 480 ? "Rechercher..." : "Mot-clé, référence, juridiction, 'expression exacte', ..."}
                                className="search-input"
                            />
                        </div>

                        <div className="search-filter-dropdown">
                            <button type="button" className="filter-button">
                                <span className="filter-icon">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
                                    </svg>
                                </span>
                                {screenSize.width > 480 ? "Décisions" : ""}
                            </button>
                        </div>

                        <button type="submit" className="search-button">
                            {screenSize.width > 480 ? "Rechercher" : "GO"}
                        </button>
                    </form>
                </div>

                <div className="login-container">
                    <button className="login-button" onClick={handleLogin}>
                        Se connecter
                    </button>
                </div>
            </header>

            <MobileHeader
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                handleSearch={handleSearch}
                toggleMobileMenu={toggleMobileMenu}
                handleLogin={handleLogin}
            />

            <MobileMenu
                isOpen={mobileMenuOpen}
                closeMenu={closeMobileMenu}
                activeTab={activeTab}
                handleMenuClick={handleMenuClick}
                handleLaunchChat={handleLaunchChat}
                handleLogin={handleLogin}
                decisionSimilaireData={decisionSimilaireData}
                decisionId={id}
            />

            <div className="justice-main">
                <div
                    className={`sidebar-wrapper ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}
                    ref={sidebarRef}
                >
                    <Sidebar
                        activeTab={activeTab}
                        handleMenuClick={handleMenuClick}
                    />
                </div>

                <main
                    className="justice-content"
                    style={{
                        ...mainContentStyle,
                        height: screenSize.width <= 768 ? 'auto' : 'calc(100vh - 80px)'
                    }}
                    ref={mainContentRef}
                >
                    {decisionData && (
                        <h1 className="decision-title">
                            {decisionData.type_decision} {decisionData.date_emission} {decisionData.reference}
                        </h1>
                    )}

                    {loading ? (
                        <div className="loading-indicator">Chargement...</div>
                    ) : error ? (
                        <div className="error-message">{error}</div>
                    ) : decisionData && (
                        <div className="decision-sections">
                            <div id="synthese" className="decision-section">
                                <h2 className="section-title">Synthèse</h2>
                                <Synthese decisionData={decisionData} />
                            </div>

                            <div id="faits_procedures" className="decision-section">
                                <h2 className="section-title">Faits et procédures</h2>
                                <FaitsProcedures decisionData={decisionData} />
                            </div>

                            <div id="motifs" className="decision-section">
                                <h2 className="section-title">Motifs</h2>
                                <Motifs decisionData={decisionData} />
                            </div>

                            <div id="references_legales" className="decision-section">
                                <h2 className="section-title">Références légales</h2>
                                <ReferencesLegales decisionData={decisionData} />
                            </div>

                            <div id="resume" className="decision-section">
                                <h2 className="section-title">Résumé</h2>
                                <Resume decisionData={decisionData} />
                            </div>

                            <div id="decision_complet" className="decision-section">
                                <h2 className="section-title">Décision complète</h2>
                                <DecisionComplete decisionData={decisionData} />
                            </div>
                        </div>
                    )}
                </main>

                <aside className="justice-sidebar-right">
                    <Card className="chatbot-card">
                        <CardContent>
                            <div className="chatbot-header">
                                <div className="chatbot-avatar">
                                    <span>DA</span>
                                </div>
                                <div className="chatbot-info">
                                    <h3 className="chatbot-title">Assistant IA</h3>
                                    <p className="chatbot-subtitle">Comprenez mieux avec l'intelligence artificielle</p>
                                </div>
                            </div>
                            <Button
                                onClick={handleLaunchChat}
                                className="chat-launch-button"
                            >
                                <MessageSquare size={18} />
                                <span>Lancer la discussion</span>
                            </Button>
                        </CardContent>
                    </Card>

                    <div className="decision-history">
                        <h3 className="history-title">Décisions similaires</h3>
                        {decisionSimilaireData && decisionSimilaireData.length > 0 ? (
                            <>
                                <ul className="history-list">
                                    {decisionSimilaireData.slice(0, 2).map((decision, index) => (
                                        <li key={decision.id || index}>
                                            <Link to={`/decision/${decision.id}`}>
                                                {decision.type_decision || "Décision"}, {decision.date_emission || "N/A"}, {decision.reference || "N/A"}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                                {decisionSimilaireData.length > 2 && (
                                    <div className="mt-3 text-center">
                                        <Button
                                            onClick={() => navigate(`/allresult?decision-id=${id}`)}
                                            className=""
                                        >
                                            <span>Voir toutes les décisions similaires</span>
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="no-data">Aucune décision similaire disponible</p>
                        )}
                    </div>

                    <div className="decision-history">
                        <h3 className="history-title">Décisions liées</h3>
                        {decisionLieeData && decisionLieeData.length > 0 ? (
                            <>
                                <ul className="history-list">
                                    {decisionLieeData.slice(0, 3).map((decision, index) => (
                                        <li key={decision.decision_id || decision.id || index}>
                                            <Link to={`/decision/${decision.decision_id || decision.id}`}>
                                                <div className="decision-item">
                                                    <div className="decision-main-info">
                                                        {decision.type_decision || "Décision"}, {decision.date_emission || "N/A"}
                                                    </div>
                                                    <div className="decision-reference">
                                                        {decision.reference || "N/A"}
                                                    </div>
                                                    <div className="decision-juridiction">
                                                        {decision.juridiction_id?.name || decision.juridiction || "Juridiction inconnue"}
                                                    </div>
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                                {decisionLieeData.length > 3 && (
                                    <div className="mt-3 text-center">
                                        <Button
                                            onClick={() => navigate(`/allresult?linked-decision-id=${id}`)}
                                            className=""
                                        >
                                            <span>Voir toutes les décisions liées ({decisionLieeData.length})</span>
                                        </Button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <p className="no-data">Aucune décision liée disponible</p>
                        )}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default DetailDecision;